import angular, { IScope } from 'angular';
import { SharedAngular } from '../../../../@types/sharedAngular';
import { Guid } from '@Shared.Angular/@types/guid';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import IAddressProvider from '@Shared.Angular/@types/core/contracts/queryModel/addressProvider/addressProvider';
import {
  IExternalBusinessProviderInfo,
  ISubFieldVisibility
} from '@Shared.Angular/@types/core/contracts/queryModel/externalBusinessProvider/externalBusinessProvider';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import FieldDataSource from '@Shared.Angular/@types/fieldDataSource';
import IFormThemeDetail, {
  FormThemeFontColorType
} from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeDetail';

const DEFAULT_SUBFIELD_VISIBILITY: ISubFieldVisibility = {
  entityName: true,
  id: true,
  industryClassificationCode: true,
  industryDescription: true,
  entityStatus: false,
  businessType: false,
  entityType: false,
  registrationDate: false,
  directorInformation: false,
  registeredAddress: false
};

declare interface IPropertiesScope extends IScope {
  field: any;
  flowModelId?: Guid;
  procedures?: IProcedureSummary[];
  addressProviderOptions?: IAddressProvider[];
  businessProviderOptions?: IExternalBusinessProviderInfo[];
}

type KendoEditorToolItem =
  | string
  | {
      name: string;
      tooltip?: string;
      exec?: (e: any) => void;
      template?: string;
      palette?: string[] | null;
    };

angular.module('fg').directive('fgEditCanvasFieldProperties', [
  'fgUtils',
  'flowinglyKBService',
  'fileService',
  'workflowStore',
  'flowinglyModelUtilityService',
  'workflowFieldVariableService',
  'flowinglyConstants',
  'procedureApiService',
  'addressLookupService',
  'externalBusinessLookupService',
  'APP_CONFIG',
  'themeService',
  '$timeout',
  function (
    fgUtils,
    flowinglyKBService,
    fileService: SharedAngular.FileService,
    workflowStore,
    flowinglyModelUtilityService: SharedAngular.FlowinglyModelUtilityService,
    workflowFieldVariableService,
    flowinglyConstants: SharedAngular.FlowinglyConstants,
    procedureApiService: SharedAngular.ProcedureApiService,
    addressLookupService: SharedAngular.AddressLookupService,
    externalBusinessLookupService: SharedAngular.ExternalBusinessLookupService,
    APP_CONFIG: SharedAngular.APP_CONFIG,
    themeService: SharedAngular.ThemeService,
    $timeout: angular.ITimeoutService
  ) {
    return {
      templateUrl:
        'angular-form-gen/edit/canvas/field/properties/properties.ng.html',
      scope: {
        field: '=fgEditCanvasFieldProperties',
        allFields: '=allFields',
        nodeKey: '=?',
        nodeId: '=?',
        flowModelId: '<?',
        theme: '=?'
      },
      link: {
        post: function ($scope: IPropertiesScope) {
          $scope.$watch('fieldPropertiesForm.$invalid', function (newValue) {
            $scope.field.$$_invalid = newValue;
          });

          $scope.field.nodeId = $scope.nodeId;

          if (APP_CONFIG.allowWorkflow) {
            $scope.$watch(
              () => $scope.theme,
              (theme: IFormThemeDetail) => {
                if (!theme) {
                  return;
                }
                setForeColorForInstructionEditor(theme);

                const field = $scope.field;
                field.value = themeService.replaceNonThemeColors(
                  field.value,
                  theme
                );
              }
            );
          }

          setForeColorForInstructionEditor(null);

          function setForeColorForInstructionEditor(
            theme: IFormThemeDetail | null
          ) {
            const isInstruction =
              $scope.field?.type ===
              flowinglyConstants.formFieldType.INSTRUCTION;
            if (!isInstruction) return;

            const editorOptions = getInstructionEditorOptions();

            const useCustomFontColors =
              theme?.fontColorType === FormThemeFontColorType.Custom;
            const foreColorItem = useCustomFontColors
              ? { name: 'foreColor', palette: theme.fontColors }
              : { name: 'foreColor', palette: null };

            // Insert the forecolor before the backcolor for consistency (index chosen intentionally)
            const FORECOLOR_INSERT_INDEX = 16;
            editorOptions.splice(FORECOLOR_INSERT_INDEX, 0, foreColorItem);

            $scope.options = editorOptions;

            updateKendoEditorTools($scope.field.name, editorOptions);
          }

          function updateKendoEditorTools(
            fieldName: string,
            editorOptions: KendoEditorToolItem[]
          ) {
            // Defer editor tools update until after DOM rendering
            $timeout(() => {
              const editorSelector = `#${fieldName}-textarea`;
              const editorElement = angular.element(editorSelector);
              if (!editorElement.length) return;

              const editor = editorElement.data('kendoEditor');
              if (!editor) return;

              editor.setOptions({ tools: editorOptions });
              editor.refresh();
            });
          }

          function getInstructionEditorOptions(): KendoEditorToolItem[] {
            return [
              'formatting',
              'bold',
              'italic',
              'underline',
              'fontName',
              'fontSize',
              'strikethrough',
              'cleanFormatting',
              'justifyLeft',
              'justifyCenter',
              'justifyRight',
              'justifyFull',
              'insertUnorderedList',
              'insertOrderedList',
              'indent',
              'outdent',
              { name: 'backColor', palette: null },
              'createLink',
              'unlink',
              'insertImage',
              'insertFile',
              {
                name: 'insertVideo',
                tooltip: 'Embed Youtube Video',
                exec: (e) =>
                  $scope.$broadcast('EditorYoutubeClicked', {
                    event: e,
                    selector: `#${$scope.field.name}-textarea`
                  })
              },
              'createTable',
              'tableWizard',
              'addRowAbove',
              'addRowBelow',
              'addColumnLeft',
              'addColumnRight',
              'deleteRow',
              'deleteColumn',
              'mergeCellsHorizontally',
              'mergeCellsVertically',
              'splitCellHorizontally',
              'splitCellVertically',
              'tableAlignLeft',
              'tableAlignCenter',
              'tableAlignRight',
              'viewHtml',
              'separator',
              {
                name: 'customTemplate',
                template: `<variable-select
                    ng-show="showAddVariableButton"
                    enabled="true"
                    target-id="field.name"
                    steps="allStepVariables"
                    on-value-selected="addVariable"
                  />`
              }
            ];
          }

          if (
            $scope.field.type === flowinglyConstants.formFieldType.INSTRUCTION
          ) {
            const restyleCheckBoxes = function (form, targetClasses) {
              // FLOW-6400 -To get checkbox in Insert Hyperlink kendo pop-up to style similar to others in Flowingly,
              // removing the kendo checkbox default class so that global Flowingly css checkbox style will take effect.
              targetClasses.forEach((selector) => {
                const linkTarget = form.find(selector);
                if (linkTarget) {
                  linkTarget.removeClass('k-checkbox');
                }
              });
            };

            const editorExecute = function (e) {
              setTimeout(function () {
                if (e.name === 'createlink') {
                  const form = e.command._dialog.element;
                  const selectors = ['#k-editor-link-target'];
                  restyleCheckBoxes(form, selectors);
                }
              }, 0);
            };

            const setupVariables = function () {
              const nodeDataArray = workflowStore.getNodeDataArray();
              const currentNode = flowinglyModelUtilityService.getNodeById(
                nodeDataArray,
                $scope.nodeId
              );
              $scope.showAddVariableButton = !(
                currentNode.taskType === ActivityTaskType.PUBLIC_FORM ||
                fgUtils.isProcessMap()
              );
              $scope.allStepVariables =
                workflowFieldVariableService.getPriorNodesVariables(
                  $scope.nodeId,
                  null
                );
            };

            // When a configured theme is deleted or unpublished while being used in a form,
            // ensure that the instruction field value is updated to the default theme font color
            if (APP_CONFIG.allowWorkflow && $scope.theme) {
              $scope.field.value = themeService.replaceNonThemeColors(
                $scope.field.value,
                $scope.theme
              );
            }

            fileService
              .replaceFileIdsWithImages($scope.field.value)
              .then((result) => {
                $scope.field.value = result;
              });

            $scope.$on('kendoWidgetCreated', function (event, widget) {
              widget.bind('paste', function (e) {
                if (e.sender && e.html) {
                  // Remove the data-file-id attribute generated for the image source from the newly inserted image through pasting.
                  e.html = e.html.replace(/data-file-id="[^"]*"/g, '');
                  const newHtml = processPastedContent(e.html);
                  if (newHtml !== null) {
                    e.html = newHtml;
                  }
                }
              });
              //to append third party fonts we need to access iframe->head object inside kendo editor and link font.
              angular
                .element(
                  angular
                    .element('.k-editable-area .k-content')[0]
                    .contentDocument.getElementsByTagName('head')
                )
                .append(
                  "<link href='https://fonts.googleapis.com/css?family=Open+Sans:400,600,700' rel='stylesheet' type='text/css'>"
                );

              const styleTag =
                widget.body.parentNode.getElementsByTagName('style')[0];
              styleTag.parentNode.removeChild(styleTag);
              widget.body.style.fontFamily = '"Open Sans", sans-serif';
              widget.body.style.fontSize = '13px';
              widget.body.style.color = '#35384D';

              // Find all the text editor and bind the execute function.
              const editorElements = $('[data-text-editor="instruction"]');

              try {
                editorElements.toArray().forEach((element) => {
                  const editor = $(element).data('kendoEditor');
                  if (editor) {
                    editor.bind('execute', editorExecute);
                  }
                });
              } catch (e) {
                // Swallow error and prevent code termination.
              }

              $timeout(() => {
                if (widget.body) {
                  $(widget.body)
                    .off('keydown.fgTabIndent')
                    .on('keydown.fgTabIndent', function (e) {
                      if (e.keyCode === 9) {
                        e.preventDefault();
                        e.stopPropagation();
                        widget.exec(e.shiftKey ? 'outdent' : 'indent');
                        widget.focus();
                      }
                    });
                }
              });
            });

            $scope.allStepVariables = [];
            $scope.showAddVariableButton = true;
            setupVariables();

            $scope.addVariable = function (variable: string, inputId: string) {
              const editor = $(
                '.field-settings .custom-email-editor textarea.k-content'
              ).data('kendoEditor');
              editor.exec('inserthtml', { value: `${variable}` });
            };
          } else if ($scope.field.type === 'selectlist') {
            $scope.onChangeCallback = function () {
              initializeFieldDataSource();
            };
            initializeFieldDataSource();
          } else if ($scope.field.type === 'lookup') {
            if (!$scope.field.lookupConfig) {
              $scope.field.lookupConfig = {
                dbName: '',
                displayValue: '',
                queryValue: '',
                value: undefined
              };
            }
          } else if ($scope.field.type === 'formula') {
            flowinglyKBService
              .getKbLink('properties.' + $scope.field.type)
              .then(function (data) {
                $scope.kbLink = data;
              });
          } else if ($scope.field.type === 'attachdocument') {
            //place all attach document properties here
          } else if (
            $scope.field.type === flowinglyConstants.formFieldType.PROCEDURE
          ) {
            setProcedures();
          } else if (
            $scope.field.type === flowinglyConstants.formFieldType.ADDRESS
          ) {
            if (!$scope.field.addressProviderId) {
              $scope.field.addressProviderId = null;
            }
            setAddressProviders();
          } else if (
            $scope.field.type ===
            flowinglyConstants.formFieldType.EXTERNAL_BUSINESS
          ) {
            initializeBusinessField($scope.field);
            setBusinessProviders();
          }

          $scope.renderInfo = fgUtils.getRenderInfo($scope.field);

          $scope.initializeFieldDataSource = initializeFieldDataSource;
          function initializeFieldDataSource() {
            const dsType = $scope.field.dataSource;
            const isManualType = !dsType || dsType == FieldDataSource.MANUALLY;

            $scope.dataSourceOptions = [
              {
                value: FieldDataSource.MANUALLY,
                text: 'Populate manually'
              },
              {
                value: FieldDataSource.DATABASE,
                text: 'Database'
              }
            ];

            if (isManualType) {
              $scope.field.dataSource = FieldDataSource.MANUALLY;
              $scope.field.dbDataSource = undefined;
              $scope.field.hideSearchableToggle = false;
            } else {
              $scope.field.searchable = true;
              $scope.field.hideSearchableToggle = true;
              if (!$scope.field.dbDataSource) {
                $scope.field.dbDataSource = {
                  dbName: '',
                  displayValue: '',
                  filters: [{}]
                };
              }
            }
          }

          function processPastedContent(html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const links = doc.querySelectorAll('a');

            links.forEach((link) => {
              const url = new URL(link.href, window.location.origin);
              // Remove the prefix if it matches the current origin and pathname
              if (
                url.origin === window.location.origin &&
                url.pathname.startsWith(window.location.pathname)
              ) {
                let newHref = url.href.replace(
                  window.location.origin + window.location.pathname,
                  ''
                );

                newHref = decodeURIComponent(newHref);
                link.href = newHref;
              } else {
                link.href = decodeURIComponent(link.href);
              }
            });

            return doc.body.innerHTML;
          }

          function setProcedures() {
            procedureApiService
              .getProceduresPublishedToCurrentUser()
              .then((procedures) => {
                procedures.sort((a, b) => a.name.localeCompare(b.name));
                $scope.procedures = procedures;
              });
          }

          function setAddressProviders() {
            addressLookupService.getProviders().then((providers) => {
              $scope.$applyAsync(() => {
                $scope.addressProviderOptions = providers;

                if (!$scope.field.addressProviderId && providers.length > 0) {
                  // we are hardcoding this value as the first provider for time being as we do not have any option to configure it from the UI
                  // eventually we'll have to remove this hardcoding, right now if frieghtways provider is not set for any business then we'll be sending the mock provider.
                  // if configured we'll be sending otherwise.
                  $scope.field.addressProviderId = providers[0].id;
                }
              });
            });
          }

          function initializeBusinessField(field) {
            if (!field.subFieldVisibility) {
              field.subFieldVisibility = { ...DEFAULT_SUBFIELD_VISIBILITY };
            }

            // Ensure entity name cannot be disabled
            field.subFieldVisibility.entityName = true;

            if (!field.externalBusinessProviderId) {
              field.externalBusinessProviderId = null;
            }
          }

          function setBusinessProviders() {
            externalBusinessLookupService.getProviders().then((providers) => {
              $scope.$applyAsync(() => {
                $scope.businessProviderOptions = providers;

                if (
                  !$scope.field.externalBusinessProviderId &&
                  providers.length > 0
                ) {
                  $scope.field.externalBusinessProviderId = providers[0].id;
                }
              });
            });
          }
        }
      }
    };
  }
]);
